@if (draft(); as draft) {
  <odl-dialog class="quiz-editor-dialog" [open]="true" size="large" (odl-close)="cancel()">
    <div slot="header" class="dialog-header">
      <h3>{{ dialogTitleKey | translate }}</h3>
      @if (showAiBadge()) {
        <span class="ai-badge">
          <odl-icon name="ai"></odl-icon>
          {{ 'QUESTIONS.GENERATED_BY_AI' | translate }}
        </span>
      }
      <odl-button
        id="quiz-editor-close-button"
        [description]="'CLOSE' | translate"
        (click)="cancel()"
      >
        <odl-icon name="close" slot="only-icon"></odl-icon>
      </odl-button>
    </div>

    @if (isLocked()) {
      <odl-callout variant="warning">{{ 'QUESTIONS.LOCKED_BANNER' | translate }}</odl-callout>
    }

    @if (aiState() === 'noContent') {
      <odl-callout variant="warning">{{ 'QUESTIONS.NO_CONTENT_WARNING' | translate }}</odl-callout>
      <div slot="footer" class="dialog-footer">
        <odl-button variant="primary" (click)="cancel()">
          {{ 'OK' | translate }}
        </odl-button>
      </div>
    } @else {
      @if (quizMode !== 'question') {
        <div class="name-row">
          <odl-input
            class="name-input"
            [value]="draft.name"
            [disabled]="isReadOnly()"
            (odl-input)="updateName($event)"
            >{{ 'QUESTIONS.NAME_LABEL' | translate }}</odl-input
          >
        </div>
      }

      @if (aiState() !== 'skipped') {
        <nr-quiz-ai-panel
          [seedText]="aiSeedText ?? ''"
          [competencies]="competencies()"
          [generating]="aiState() === 'generating'"
          [mode]="quizMode"
          (generate)="onGenerate($event)"
          (skip)="cancel()"
        ></nr-quiz-ai-panel>

        @if (aiState() === 'generating') {
          <div slot="footer" class="dialog-footer">
            <div class="dialog-footer-end">
              <nr-skeleton [type]="'button'"></nr-skeleton>
              <nr-skeleton [type]="'button'"></nr-skeleton>
            </div>
          </div>
        }
      } @else {
        <section class="section">
          @if (quizMode !== 'question') {
            <div class="section-header">
              <h4>{{ 'QUESTIONS.QUESTIONS_LABEL' | translate }} ({{ draft.questions.length }})</h4>
              <odl-button
                id="quiz-questions-toggle"
                [attr.aria-expanded]="questionsExpanded()"
                [description]="(questionsExpanded() ? 'COLLAPSE' : 'EXPAND') | translate"
                (click)="toggleQuestionsExpanded()"
              >
                <odl-icon
                  name="expand_more"
                  class="chevron"
                  slot="only-icon"
                  [class.chevron--expanded]="questionsExpanded()"
                ></odl-icon>
              </odl-button>
            </div>
          }

          @if (quizMode === 'question' || questionsExpanded()) {
            <div class="questions" cdkDropList (cdkDropListDropped)="reorderQuestions($event)">
              @for (question of draft.questions; track question.tempId; let qi = $index) {
                <div
                  class="question-row"
                  cdkDrag
                  [cdkDragDisabled]="isReadOnly() || quizMode === 'question'"
                >
                  @if (quizMode !== 'question' && !isReadOnly()) {
                    <div class="drag-handle" cdkDragHandle>
                      <odl-icon name="drag_indicator_2"></odl-icon>
                    </div>
                  }

                  @if (isQuestionEditable(question)) {
                    <div class="question-card">
                      <div class="question-header">
                        <odl-textarea
                          class="question-text"
                          [value]="question.text"
                          [disabled]="isReadOnly()"
                          (odl-input)="updateQuestionText(qi, $event)"
                        >
                          {{ 'QUESTIONS.QUESTION_LABEL' | translate }}
                        </odl-textarea>
                      </div>

                      @if (!isOpenEnded()) {
                        <input
                          #imageInput
                          type="file"
                          hidden
                          accept="image/jpeg,image/png,image/gif"
                          (change)="onImageSelected(qi, imageInput.files)"
                        />
                        @if (imagePreviewUrl(question); as previewUrl) {
                          <div class="image-preview">
                            <div class="image-preview-inner">
                              <img [src]="previewUrl" alt="" />
                              @if (!isReadOnly()) {
                                <odl-button
                                  variant="text"
                                  class="image-remove-button"
                                  [id]="deleteImageButtonId(question)"
                                  [description]="'QUESTIONS.DELETE_IMAGE' | translate"
                                  (click)="removeImage(qi)"
                                >
                                  <odl-icon name="delete" slot="only-icon"></odl-icon>
                                </odl-button>
                              }
                            </div>
                          </div>
                        } @else {
                          <odl-button
                            variant="text"
                            class="add-image-button"
                            [disabled]="isReadOnly()"
                            (click)="imageInput.click()"
                          >
                            <odl-icon name="image_add" slot="start"></odl-icon>
                            {{ 'QUESTIONS.ADD_IMAGE' | translate }}
                          </odl-button>
                        }
                      }

                      @if (quizMode === 'question') {
                        <div class="answer-type-row">
                          <odl-radio-group
                            [value]="answerType()"
                            (odl-input)="setAnswerType($event)"
                          >
                            <span slot="label">{{ 'QUESTIONS.ANSWER_TYPE' | translate }}</span>
                            <div class="answer-type-option">
                              <input
                                type="radio"
                                name="answer-type"
                                id="answer-type-open-ended"
                                value="openEnded"
                                [disabled]="isReadOnly()"
                              />
                              <label for="answer-type-open-ended">
                                {{ 'QUESTIONS.OPEN_ENDED_ANSWER' | translate }}
                              </label>
                            </div>
                            <div class="answer-type-option">
                              <input
                                type="radio"
                                name="answer-type"
                                id="answer-type-multiple-choice"
                                value="multipleChoice"
                                [disabled]="isReadOnly()"
                              />
                              <label for="answer-type-multiple-choice">
                                {{ 'QUESTIONS.MULTIPLE_CHOICE_ANSWER' | translate }}
                              </label>
                            </div>
                          </odl-radio-group>
                        </div>
                      }

                      @if (!isOpenEnded()) {
                        <odl-radio-group
                          [value]="question.correctChoice.toString()"
                          (odl-input)="setCorrectChoice(qi, $event)"
                        >
                          <span slot="label">{{ 'QUESTIONS.ANSWERS_LABEL' | translate }}</span>
                          @for (choice of question.choices; track $index; let ci = $index) {
                            <div class="choice-row">
                              <input
                                type="radio"
                                [name]="'correct-' + question.tempId"
                                [value]="ci"
                                [disabled]="isReadOnly()"
                              />
                              <odl-textarea
                                class="choice-text"
                                [value]="choice.text"
                                [disabled]="isReadOnly()"
                                (odl-input)="
                                  updateChoiceText(qi, ci, $event); $event.stopPropagation()
                                "
                              ></odl-textarea>
                              @if (question.choices.length > 2) {
                                <odl-button
                                  variant="text"
                                  [id]="deleteChoiceButtonId(question, ci)"
                                  [description]="'QUESTIONS.DELETE_ANSWER' | translate"
                                  [disabled]="isReadOnly()"
                                  (click)="removeChoice(qi, ci)"
                                >
                                  <odl-icon name="delete" slot="only-icon"></odl-icon>
                                </odl-button>
                              }
                            </div>
                          }
                        </odl-radio-group>
                        <div class="choice-row choice-row--add">
                          <input
                            type="radio"
                            class="choice-row-spacer"
                            disabled
                            tabindex="-1"
                            aria-hidden="true"
                          />
                          <button
                            type="button"
                            class="add-row"
                            [id]="addChoiceButtonId(question)"
                            [attr.aria-label]="'QUESTIONS.ADD_ANSWER' | translate"
                            [disabled]="isReadOnly()"
                            (click)="addChoice(qi)"
                          >
                            <odl-icon name="add_circle"></odl-icon>
                          </button>
                          @if (question.choices.length > 2) {
                            <odl-button
                              class="choice-row-spacer"
                              variant="text"
                              disabled
                              tabindex="-1"
                              aria-hidden="true"
                            >
                              <odl-icon name="delete" slot="only-icon"></odl-icon>
                            </odl-button>
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="question-card question-card--readonly">
                      <div class="question-summary-header">
                        <span class="question-number">{{ qi + 1 }}.</span>
                        <p class="question-summary-text">{{ question.text }}</p>
                        @if (!isReadOnly()) {
                          <odl-button
                            variant="text"
                            [id]="questionMenuButtonId(question)"
                            [description]="'QUESTIONS.QUESTION_OPTIONS' | translate"
                          >
                            <odl-icon name="more_vert" slot="only-icon"></odl-icon>
                          </odl-button>
                        }
                      </div>

                      @if (!isOpenEnded() && imagePreviewUrl(question); as summaryImageUrl) {
                        <img class="question-summary-image" [src]="summaryImageUrl" alt="" />
                      }

                      @if (isOpenEnded()) {
                        @if (draft.enableAiRating) {
                          <span class="ai-rating-chip">
                            <odl-icon name="ai"></odl-icon>
                            {{ 'QUESTIONS.AI_RATING_ENABLED_CHIP' | translate }}
                          </span>
                        }
                      } @else {
                        <div class="choices-readonly">
                          @for (choice of question.choices; track $index; let ci = $index) {
                            <div
                              class="choice-row choice-row--readonly"
                              [class.choice-row--locked]="isReadOnly()"
                            >
                              <input
                                type="radio"
                                disabled
                                [checked]="ci === question.correctChoice"
                              />
                              <span>{{ choice.text }}</span>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>

            @if (quizMode !== 'question') {
              <button
                type="button"
                id="quiz-question-add"
                class="add-row"
                [attr.aria-label]="'QUESTIONS.ADD_QUESTION' | translate"
                [disabled]="isReadOnly() || addingQuestionWithAi()"
                (click)="showAddQuestionAiMenu() ? null : addQuestion()"
              >
                <odl-icon name="add_circle"></odl-icon>
              </button>
            }
          }
        </section>

        <section class="section">
          <div class="section-header">
            <h4>{{ 'QUESTIONS.SETTINGS' | translate }}</h4>
            <odl-button
              id="quiz-settings-toggle"
              [attr.aria-expanded]="settingsExpanded()"
              [description]="(settingsExpanded() ? 'COLLAPSE' : 'EXPAND') | translate"
              (click)="toggleSettingsExpanded()"
            >
              <odl-icon
                name="expand_more"
                class="chevron"
                slot="only-icon"
                [class.chevron--expanded]="settingsExpanded()"
              ></odl-icon>
            </odl-button>
          </div>

          @if (settingsExpanded()) {
            <div class="settings">
              @if (competencies().length) {
                <div class="settings-row">
                  <nr-competency-picker
                    [competencies]="competencies()"
                    [disabled]="isReadOnly()"
                    [selected]="selectedCompetencies()"
                    (selectedChange)="updateCompetencies($event)"
                  ></nr-competency-picker>
                </div>
              }

              <div class="settings-row">
                <odl-checkbox
                  [checked]="hasTimeLimit()"
                  [disabled]="isReadOnly()"
                  (odl-input)="toggleTimeLimit($event)"
                >
                  {{ 'QUESTIONS.ENABLE_RESPONSE_TIME' | translate }}
                </odl-checkbox>
                @if (hasTimeLimit()) {
                  <div class="settings-row-inline">
                    <span class="settings-field-label">
                      {{ 'QUESTIONS.MINUTES' | translate }}
                    </span>
                    <odl-input
                      #timeInput
                      type="number"
                      [value]="(draft.time ?? 0).toString()"
                      [disabled]="isReadOnly()"
                      [description]="'QUESTIONS.MINUTES' | translate"
                      (odl-input)="updateTime($event, timeInput)"
                    ></odl-input>
                    <odl-checkbox
                      [checked]="draft.applyTimeAllQuizzes ?? false"
                      [disabled]="isReadOnly()"
                      (odl-input)="toggleApplyTimeAll($event)"
                    >
                      {{ 'QUESTIONS.APPLY_TIME_ALL' | translate }}
                    </odl-checkbox>
                  </div>
                }
              </div>

              @if (!isOpenEnded()) {
                @if (quizMode === 'question') {
                  <div class="settings-row">
                    <odl-checkbox
                      [checked]="draft.messyAnswers ?? false"
                      [disabled]="isReadOnly()"
                      (odl-input)="toggleMessyAnswers($event)"
                    >
                      {{ 'QUESTIONS.ENABLE_MESSY_ANSWERS' | translate }}
                    </odl-checkbox>
                  </div>
                } @else {
                  <div class="settings-row">
                    <odl-checkbox
                      [checked]="draft.randomQuestions ?? false"
                      [disabled]="isReadOnly()"
                      (odl-input)="toggleRandomQuestions($event)"
                    >
                      {{ 'QUESTIONS.ENABLE_RANDOM' | translate }}
                    </odl-checkbox>
                    @if (draft.randomQuestions) {
                      <div class="settings-field">
                        <span class="settings-field-label">
                          {{ 'QUESTIONS.NUM_RANDOM_QUESTIONS' | translate }}
                        </span>
                        <odl-input
                          #numRandomInput
                          type="number"
                          [value]="(draft.numRandomQuestions ?? 1).toString()"
                          [disabled]="isReadOnly()"
                          [description]="'QUESTIONS.NUM_RANDOM_QUESTIONS' | translate"
                          (odl-input)="updateNumRandomQuestions($event, numRandomInput)"
                        ></odl-input>
                        @if (numRandomQuestionsCapped()) {
                          <p class="settings-field-info" role="status">
                            {{ 'QUESTIONS.NUM_RANDOM_QUESTIONS_MAX' | translate }}
                          </p>
                        }
                      </div>
                    }
                  </div>
                }

                @if (canAddAttempts()) {
                  <div class="settings-row">
                    <odl-checkbox
                      [checked]="hasMultipleAttempts()"
                      [disabled]="isReadOnly()"
                      (odl-input)="toggleMultipleAttempts($event)"
                    >
                      {{ 'QUESTIONS.ENABLE_MULTIPLE_ATTEMPTS' | translate }}
                    </odl-checkbox>
                    @if (hasMultipleAttempts()) {
                      <div class="settings-field">
                        <span class="settings-field-label">
                          {{ 'QUESTIONS.ATTEMPTS' | translate }}
                        </span>
                        <odl-input
                          #attemptsInput
                          type="number"
                          [value]="(draft.maxAttempts ?? 2).toString()"
                          [disabled]="isReadOnly()"
                          [description]="'QUESTIONS.ATTEMPTS' | translate"
                          (odl-input)="updateMaxAttempts($event, attemptsInput)"
                          (odl-change)="commitMaxAttempts(attemptsInput)"
                        ></odl-input>
                      </div>
                    }
                  </div>
                }
              } @else if (showAiRatingOption()) {
                <div class="settings-row">
                  <odl-checkbox
                    [checked]="enableAiRating()"
                    [disabled]="isReadOnly()"
                    (odl-input)="toggleAiRating($event)"
                  >
                    {{ 'QUESTIONS.ENABLE_AI_RATING' | translate }}
                  </odl-checkbox>
                  @if (enableAiRating()) {
                    <div class="ai-rating-reveal">
                      <p class="ai-rating-title">{{ 'QUESTIONS.AI_GRADE_TITLE' | translate }}</p>
                      <p class="ai-rating-description">
                        {{ 'QUESTIONS.AI_GRADE_DESCRIPTION' | translate }}
                      </p>
                      <textarea
                        #referenceAnswerInput
                        id="reference-answer-input"
                        name="reference-answer-input"
                        class="reference-answer-input"
                        [disabled]="isReadOnly()"
                        [value]="draft.referenceAnswer ?? ''"
                        [attr.placeholder]="'QUESTIONS.REFERENCE_ANSWER_PLACEHOLDER' | translate"
                        [attr.aria-label]="'QUESTIONS.REFERENCE_ANSWER_PLACEHOLDER' | translate"
                        (input)="updateReferenceAnswer(referenceAnswerInput.value)"
                      ></textarea>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </section>

        <div slot="footer" class="dialog-footer">
          <div class="dialog-footer-start">
            @if (draft.id && !isReadOnly()) {
              <odl-button variant="text" [disabled]="saving()" (click)="confirmDelete.set(true)">
                {{ 'DELETE' | translate }}
              </odl-button>
            }
          </div>
          <div class="dialog-footer-end">
            @if (!isReadOnly()) {
              <odl-button variant="secondary" [disabled]="saving()" (click)="cancel()">
                {{ 'CANCEL' | translate }}
              </odl-button>
              <odl-button variant="primary" [disabled]="!isValid() || saving()" (click)="save()">
                {{ (saving() ? 'SAVING' : 'SAVE') | translate }}
              </odl-button>
            } @else if (draft.id && canManageQuestions()) {
              <odl-button
                variant="secondary"
                [disabled]="saving()"
                (click)="confirmDelete.set(true)"
              >
                {{ 'DELETE' | translate }}
              </odl-button>
            }
          </div>
        </div>
      }
    }
  </odl-dialog>

  <odl-tooltip for="#quiz-editor-close-button">
    {{ 'CLOSE' | translate }}
  </odl-tooltip>

  @if (quizMode !== 'question') {
    <odl-tooltip for="#quiz-questions-toggle">
      {{ (questionsExpanded() ? 'COLLAPSE' : 'EXPAND') | translate }}
    </odl-tooltip>
  }
  <odl-tooltip for="#quiz-settings-toggle">
    {{ (settingsExpanded() ? 'COLLAPSE' : 'EXPAND') | translate }}
  </odl-tooltip>

  @if (questionsExpanded()) {
    @if (quizMode !== 'question') {
      @if (showAddQuestionAiMenu()) {
        <odl-menu for="#quiz-question-add" placement="bottom">
          <odl-menu-item (odl-select)="startAddQuestionWithAi()">
            <odl-icon name="ai" slot="icon"></odl-icon>
            <span>{{ 'TEXT_SELECTION.WITH_AI' | translate }}</span>
          </odl-menu-item>
          <odl-menu-item (odl-select)="addQuestion()">
            <odl-icon name="add" slot="icon"></odl-icon>
            <span>{{ 'TEXT_SELECTION.MANUALLY' | translate }}</span>
          </odl-menu-item>
        </odl-menu>
      } @else {
        <odl-tooltip for="#quiz-question-add">
          {{ 'QUESTIONS.ADD_QUESTION' | translate }}
        </odl-tooltip>
      }
    }
    @for (question of draft.questions; track question.tempId) {
      @if (isQuestionEditable(question)) {
        @if (imagePreviewUrl(question) && !isReadOnly()) {
          <odl-tooltip [attr.for]="'#' + deleteImageButtonId(question)">
            {{ 'QUESTIONS.DELETE_IMAGE' | translate }}
          </odl-tooltip>
        }
        <odl-tooltip [attr.for]="'#' + addChoiceButtonId(question)">
          {{ 'QUESTIONS.ADD_ANSWER' | translate }}
        </odl-tooltip>
        @if (question.choices.length > 2) {
          @for (choice of question.choices; track $index; let ci = $index) {
            <odl-tooltip [attr.for]="'#' + deleteChoiceButtonId(question, ci)">
              {{ 'QUESTIONS.DELETE_ANSWER' | translate }}
            </odl-tooltip>
          }
        }
      } @else {
        <odl-tooltip [attr.for]="'#' + questionMenuButtonId(question)">
          {{ 'QUESTIONS.QUESTION_OPTIONS' | translate }}
        </odl-tooltip>
        <odl-menu [attr.for]="'#' + questionMenuButtonId(question)" placement="bottom-end">
          <odl-menu-item (odl-select)="expandQuestion(question)">
            <odl-icon name="edit" slot="icon"></odl-icon>
            <span>{{ 'EDIT' | translate }}</span>
          </odl-menu-item>
          @if (draft.questions.length > 1) {
            <odl-menu-item (odl-select)="removeQuestion($index)">
              <odl-icon name="delete" slot="icon"></odl-icon>
              <span>{{ 'DELETE' | translate }}</span>
            </odl-menu-item>
          }
        </odl-menu>
      }
    }
  }

  @if (confirmDelete()) {
    <odl-dialog
      class="quiz-delete-dialog"
      [label]="deleteDialogTitleKey | translate"
      [open]="true"
      (odl-close)="confirmDelete.set(false)"
    >
      {{ deleteDialogBodyKey | translate }}
      <div slot="footer" class="dialog-footer">
        <odl-button variant="secondary" (click)="confirmDelete.set(false)">
          {{ 'CANCEL' | translate }}
        </odl-button>
        <odl-button variant="primary" (click)="confirmDeleteQuiz()">
          {{ 'DELETE' | translate }}
        </odl-button>
      </div>
    </odl-dialog>
  }
}
